import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import authApi from '../../services/api/auth.api';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import {
  ChartBarIcon,
  LockClosedIcon,
  ArrowRightIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { config: whiteLabelConfig, shouldShowWhiteLabel } = useWhiteLabel();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError(t('resetPassword.invalidLink') || 'Invalid or missing reset link');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      const errorMsg = t('resetPassword.passwordMismatch') || 'Passwords do not match';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      const errorMsg = t('resetPassword.passwordTooShort') || 'Password must be at least 6 characters';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (!token) {
      const errorMsg = t('resetPassword.invalidLink') || 'Invalid reset link';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.resetPassword(token, formData.password);

      if (response.success) {
        setSuccess(true);
        toast.success(t('resetPassword.success') || 'Password reset successfully!', {
          icon: '✅',
          duration: 5000,
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const errorMsg = t('resetPassword.error') || 'Failed to reset password';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || t('resetPassword.error') || 'An error occurred';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-12 flex-col justify-between">
        <div>
          {shouldShowWhiteLabel && whiteLabelConfig.custom_name ? (
            <div className="flex items-center gap-3 text-white">
              <ChartBarIcon className="h-10 w-10" />
              <span className="text-2xl font-bold">{whiteLabelConfig.custom_name}</span>
            </div>
          ) : (
            <Link to="/" className="flex items-center gap-3 text-white">
              <ChartBarIcon className="h-10 w-10" />
              <span className="text-2xl font-bold">Kubmix Flux</span>
            </Link>
          )}
        </div>

        <div className="text-white">
          <h1 className="text-5xl font-bold mb-6">
            {t('resetPassword.hero.title')}
            <br />
            {t('resetPassword.hero.titleLine2')}
          </h1>
          <p className="text-xl text-indigo-100 mb-8">
            {t('resetPassword.hero.subtitle')}
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                ✓
              </div>
              <span className="text-lg">{t('resetPassword.hero.feature1')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                ✓
              </div>
              <span className="text-lg">{t('resetPassword.hero.feature2')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                ✓
              </div>
              <span className="text-lg">{t('resetPassword.hero.feature3')}</span>
            </div>
          </div>
        </div>

        <div className="text-indigo-100 text-sm">
          {t('resetPassword.hero.copyright')}
        </div>
      </div>

      {/* Right Side - Reset Password Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        {/* Language Switcher */}
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            {shouldShowWhiteLabel && whiteLabelConfig.custom_name ? (
              <div className="inline-flex items-center gap-2">
                <ChartBarIcon className="h-8 w-8 text-indigo-600" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{whiteLabelConfig.custom_name}</span>
              </div>
            ) : (
              <Link to="/" className="inline-flex items-center gap-2">
                <ChartBarIcon className="h-8 w-8 text-indigo-600" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">Kubmix Flux</span>
              </Link>
            )}
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('resetPassword.title') || 'Reset your password'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('resetPassword.subtitle') || 'Enter a new password for your account.'}
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
              <div className="flex items-start gap-3">
                <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                    {t('resetPassword.successTitle') || 'Password reset successful!'}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {t('resetPassword.redirecting') || 'Redirecting to login page...'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!success && token && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('resetPassword.newPassword') || 'New password'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="block w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                    placeholder={t('resetPassword.passwordPlaceholder') || 'Enter new password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('resetPassword.passwordHint') || 'Must be at least 6 characters'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('resetPassword.confirmPassword') || 'Confirm password'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="block w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                    placeholder={t('resetPassword.confirmPasswordPlaceholder') || 'Confirm new password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t('resetPassword.resetting') || 'Resetting...'}
                  </>
                ) : (
                  <>
                    {t('resetPassword.resetButton') || 'Reset password'}
                    <ArrowRightIcon className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>
          )}

          {success && (
            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                {t('resetPassword.goToLogin') || 'Go to login'}
              </Link>
            </div>
          )}

          {!success && (
            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                    {t('resetPassword.needHelp') || 'Need help?'}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex items-center justify-center px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-base font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('resetPassword.backToLogin') || 'Back to login'}
                </Link>
              </div>
            </div>
          )}

          <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
            <Link to="https://flux.devskin.com" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
              {t('resetPassword.backToHome') || '← Back to home'}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
